import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { capturePayPalOrder } from '../lib/api';
import api from '../lib/api';
import { readPendingPayPal, clearPendingPayPal } from '../lib/inAppBrowser';

const CAPTURED_KEY = 'parachpay_pp_captured';

const markCaptured = (orderId) => {
  try { sessionStorage.setItem(`${CAPTURED_KEY}_${orderId}`, '1'); } catch (e) { /* ignore */ }
};
const wasCaptured = (orderId) => {
  try { return sessionStorage.getItem(`${CAPTURED_KEY}_${orderId}`) === '1'; } catch (e) { return false; }
};

const fmt = (amount, currency) => `${currency} ${Number(amount).toFixed(2)}`;

export default function DepositPayPalReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get('token');
  const cancelled = params.get('cancelled') === '1' || !orderId;
  const [state, setState] = useState(cancelled ? 'cancelled' : 'verifying');
  const [result, setResult] = useState(null);
  const started = useRef(false);

  const loadCompletedOrder = useCallback(async () => {
    const { data } = await api.get(`/paypal/orders/${orderId}`);
    if (data.status !== 'COMPLETED') throw new Error('not completed');
    setResult({ amount: data.amount, currency: data.currency });
    setState('success');
  }, [orderId]);

  const verify = useCallback(async () => {
    setState('verifying');
    try {
      if (wasCaptured(orderId)) {
        await loadCompletedOrder();
        return;
      }
      const { data } = await capturePayPalOrder(orderId);
      markCaptured(orderId);
      setResult({ amount: data.amount, currency: data.currency, balance: data.new_balance });
      setState('success');
    } catch (e) {
      try {
        await loadCompletedOrder();
        markCaptured(orderId);
      } catch (inner) {
        setState('error');
      }
    } finally {
      const pending = readPendingPayPal();
      if (!pending || pending.orderId === orderId) clearPendingPayPal();
    }
  }, [orderId, loadCompletedOrder]);

  useEffect(() => {
    if (cancelled) {
      clearPendingPayPal();
      return;
    }
    if (started.current) return;
    started.current = true;
    verify();
  }, [cancelled, verify]);

  const view = {
    verifying: { icon: <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />, title: 'Confirming your payment…', text: 'Please keep this screen open. This usually takes a few seconds.' },
    success: { icon: <CheckCircle2 className="h-12 w-12 text-emerald-500" />, title: 'Deposit successful', text: result ? `${fmt(result.amount, result.currency)} has been added to your ParachPay wallet.` : '' },
    cancelled: { icon: <XCircle className="h-12 w-12 text-muted-foreground" />, title: 'Payment cancelled', text: 'You closed PayPal before paying. You have not been charged.' },
    error: { icon: <AlertCircle className="h-12 w-12 text-amber-500" />, title: "We couldn't confirm your payment yet", text: "If you completed the payment, tap Check again. If PayPal shows no payment, you have not been charged." },
  }[state];

  return (
    <Layout>
      <div className="max-w-md mx-auto pt-8">
        <Card className="bg-card border-border">
          <CardContent className="pt-8 pb-8 text-center space-y-4" data-testid={`paypal-return-${state}`}>
            <div className="flex justify-center">{view.icon}</div>
            <h1 className="text-xl font-semibold" data-testid="paypal-return-title">{view.title}</h1>
            <p className="text-sm text-muted-foreground" data-testid="paypal-return-message">{view.text}</p>
            {state === 'success' && result?.balance !== undefined && (
              <p className="text-sm" data-testid="paypal-return-balance">New {result.currency} balance: <strong>{Number(result.balance).toFixed(2)}</strong></p>
            )}
            <div className="flex flex-col gap-2 pt-2">
              {state === 'success' && (
                <Button onClick={() => navigate('/dashboard')} data-testid="paypal-return-dashboard-btn">Go to dashboard</Button>
              )}
              {state === 'cancelled' && (
                <Button onClick={() => navigate('/deposit')} data-testid="paypal-return-try-again-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />Try again
                </Button>
              )}
              {state === 'error' && (
                <>
                  <Button onClick={verify} data-testid="paypal-return-check-again-btn">
                    <RefreshCw className="h-4 w-4 mr-2" />Check again
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/deposit')} data-testid="paypal-return-back-btn">Back to Deposit</Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
